/**
 * The permission grammar every credential speaks: `<model>:<verb>`.
 *
 * A request asks two different questions, and they are answered in different places:
 *
 * | Question | Answered by | Where |
 * |---|---|---|
 * | which projects/org may this principal touch? | membership rows → the autogen access tables | the guards, in SQL, on every query |
 * | which operation may this credential perform? | the scope set on the credential | once, at the boundary, before any SQL |
 *
 * Nothing in here reaches `Repo.ts` or the SQL guards, so an action's internal reads are
 * unaffected: a `card:write` token still reads the decks `cards.update` walks through.
 *
 * A **scope** is what an action requires; a **role** is a closed list of scopes. Nothing implies
 * anything — `write` does not grant `read` — so a role that should do both lists both.
 *
 * Both packages import this file, so the app derives the same two sets dispatch does rather than
 * holding a copy of them. Two rules follow, neither of which a tool enforces:
 *
 * - It imports nothing from `node-api` or `web-app`. A `node:` import would break the app build
 *   loudly; a type import from a sibling package would not.
 * - Nothing runs at module level except the two table checks below, which are set lookups and run
 *   in the browser too.
 *
 * What only a request needs — `effectiveScopes`, the add-on roles each token kind may carry, the
 * read-side grant and its error — stays in `node-api/src/context/auth/scopes.ts`.
 */

/** A person's membership in an org, `account_users.role`. */
export type UserRole =
  | "owner"
  | "admin"
  | "guest"
  | "service"
  | "deleted"
  | "disabled"
  | "observer"
  // an api token's principal: project-scoped like an observer, but writes when its scopes allow it,
  // never a seat, never shown in a people list
  | "token";

/** A person's standing in one project, `project_users.role`. */
export type UserProjectRole = "default" | "producer";

/**
 * Field tiers within a model, as declared by `group` in the shared model JSON.
 *
 * Unannotated is `default`, which any member reads; `public` is the opt-in tier an Open Decks
 * visitor reads too; `self` and `selfSecurity` are read on the caller's own `user` row only. A
 * non-default group becomes its own verb (`account:readAdmin`), never a rank.
 */
export const SCOPE_GROUPS = ["public", "default", "admin", "self", "selfSecurity"] as const;
export type ScopeGroup = (typeof SCOPE_GROUPS)[number];

const SCOPE_GROUP_SET: ReadonlySet<string> = new Set(SCOPE_GROUPS);

export const isScopeGroup = (value: string): value is ScopeGroup => SCOPE_GROUP_SET.has(value);

const SELF_SCOPE_GROUPS: readonly ScopeGroup[] = ["self", "selfSecurity"];

/** Whether the group's fields are nulled on every `user` row but the caller's own. */
export const isSelfScopeGroup = (group: ScopeGroup): boolean => SELF_SCOPE_GROUPS.includes(group);

const readVerb = (group: ScopeGroup): string =>
  group === "default" ? "read" : `read${group[0].toUpperCase()}${group.slice(1)}`;

/**
 * The scope a read of `modelName` needs, given the field tier the key was declared in.
 *
 * `default` → `<model>:read`; any other group appends the capitalized group, so
 * `account.billingEmail` wants `account:readAdmin`.
 *
 * A model outside the registry yields a string no role holds — a token is told what it would need.
 */
export const modelReadScope = (modelName: string, group: ScopeGroup = "default"): Scope =>
  `${modelName}:${readVerb(group)}` as Scope;

const SELF_GROUP_READ_VERBS: ReadonlySet<string> = new Set(SELF_SCOPE_GROUPS.map(readVerb));

/** Whether `scope` is the read of a self group, e.g. `user:readSelf`. */
export const isSelfGroupScope = (scope: Scope): boolean =>
  SELF_GROUP_READ_VERBS.has(scope.slice(scope.indexOf(":") + 1));

/**
 * Content models a token principal may read. An absent model is unreachable, which is why billing,
 * integration config and token management need no special-casing.
 *
 * The hand (`queueEntry`) and bookmarks (`handCard`) are in: they are work assignment, which
 * automations do coordinate. Per-user state is in `SELF_MODELS` instead.
 */
const PROJECT_MODELS = [
  "_root",
  "account",
  "accountRole",
  "activity",
  "assigneeAssignment",
  "assigneeDeckAssignment",
  "attachment",
  "card",
  "cardHistory",
  "cardOrder",
  "cardOrderInDeck",
  "cardPreset",
  "cardSubscription",
  "cardUpvote",
  "cardsEffortHistory",
  "cardsFinishedHistory",
  "cardsStatusHistory",
  "cardsTimeToFinished",
  "deck",
  "deckAssignment",
  "deckGuardian",
  "deckSubscription",
  "dueCard",
  "file",
  "forecastOverwrite",
  "handCard",
  "milestone",
  "milestoneProgress",
  "milestoneProject",
  "pinnedMilestone",
  "project",
  "projectOrder",
  "projectTag",
  "projectUser",
  "projectUserSetting",
  "publicProjectInfo",
  "queueEntry",
  "release",
  "resolvable",
  "resolvableEntry",
  "resolvableEntryHistory",
  "resolvableEntryReaction",
  "resolvableParticipant",
  "resolvableParticipantHistory",
  "sprint",
  "sprintConfig",
  "sprintConfigProgress",
  "sprintProgress",
  "sprintProject",
  "user",
  "userTag",
  "visionBoard",
  "visionBoardQuery",
  "workflowItem",
  "workflowItemHistory",
  "workflowTemplate",
  "workflowTemplateItem",
] as const;

/**
 * Content models a token principal and a member read, and an Open Decks visitor does not even on a
 * public card: who worked on it, and for how long. Their graph guard is `card:team` to match.
 */
const PROJECT_MEMBER_MODELS = ["timeTrackingSegment", "timeTrackingSum"] as const;

/**
 * Models anyone who may write content at all may write.
 *
 * `queueEntry` is here rather than at the admin tier: a token has no hand of its own, so every hand
 * write is another user's anyway, and `queue_entries`' write guard already has no per-user check.
 * Tighten a single token with `removeScopes`.
 *
 * Only models some action declares. Rows written inside another action — assignments, a card's
 * order in a deck — mint no scope, since nothing would ever ask for it.
 */
const USER_WRITABLE_MODELS = [
  "attachment",
  "card",
  "cardOrder",
  "cardPreset",
  "cardSubscription",
  "cardUpvote",
  "deckSubscription",
  "file",
  "handCard",
  "pinnedMilestone",
  "queueEntry",
  "resolvable",
  "resolvableEntry",
  "resolvableEntryReaction",
  "resolvableParticipant",
  "timeTrackingSegment",
  "userTag",
  "visionBoard",
  "visionBoardQuery",
] as const;

/**
 * A project's structure rather than its content: what a producer edits in the project they
 * produce. All but the journey template are project-scoped, and the descriptor answers them per
 * project. The join rows (`milestoneProject`, `sprintProject`) and template items are written
 * inside their parent's action and mint no scope.
 *
 * `deckGuardian` is here rather than with the content models: who guards a deck is a producer's
 * call. Getting past a guardian list is `project:write`, see `ensureGuardianRule`.
 */
const PRODUCER_WRITABLE_MODELS = [
  "deck",
  "deckGuardian",
  "forecastOverwrite",
  "milestone",
  "project",
  "projectTag",
  "projectUser",
  "sprint",
  "sprintConfig",
  "workflowItem",
  "workflowTemplate",
] as const;

/**
 * Models every credential may read, an anonymous Open Decks reader included. The guards confine
 * which *rows* come back (`is_public`, `comments_are_public`); this list confines which *keys* may
 * be named at all.
 */
const VISITOR_MODELS = [
  ...PROJECT_MODELS,
  "stripeAccountSync",
  "discordGuild",
  "publicProjectMembership",
  "publicProjectVisit",
  "dailyPublicProjectMembership",
] as const;

/**
 * Models whose fields carry a `group: "public"` tier. A visitor holds `<model>:readPublic` for
 * these rather than `<model>:read`, which is the member tier. The importer refuses to boot when the
 * models with a `public` field differ from this list.
 */
export const PUBLIC_TIER_MODELS = [
  "account",
  "stripeAccountSync",
  "discordGuild",
] as const satisfies readonly (typeof VISITOR_MODELS)[number][];

type PublicTierModel = (typeof PUBLIC_TIER_MODELS)[number];

const PUBLIC_TIER_MODEL_SET: ReadonlySet<string> = new Set(PUBLIC_TIER_MODELS);

export const isPublicTierModel = (modelName: string): modelName is PublicTierModel =>
  PUBLIC_TIER_MODEL_SET.has(modelName);

/**
 * Models a member of the org may read. Their walls are elsewhere: a SQL guard on the row, or the
 * `group: "admin"` on the relation that lists them (`account.accountApiTokens`), which needs
 * `account:readAdmin` of its own.
 */
const MEMBER_MODELS = [
  "accountOnboarding",
  "accountOnboardingStep",
  "affiliateCodeStat",
  "dailyDiscordGuildVoteMembership",
  "discordMember",
  "discordProjectNotification",
  "discordSlashCommand",
  "integration",
  "jiraProjectSync",
  "jiraSite",
  "userInvitation",
  "userInviteCode",
  "userEmail",
  "userProjectAccess",
  "userReportEmail",
  "wizard",
] as const;

/**
 * Models an admin of the org reads, and the token `admin` tier with it. Their graph guards ask for
 * the scope as well, because a human's ceiling only warns on a miss until 2026-12-31.
 */
const ADMIN_READ_MODELS = ["invoice", "affiliateCode"] as const;

/**
 * Models an admin of the org may write, and no token role holds. An org token reaches
 * `affiliateCode:write` through the `affiliateManager` add-on role, so a script can mint referral
 * codes without being an admin in every other respect.
 */
const ADMIN_WRITABLE_MODELS = ["affiliateCode"] as const;

/**
 * Models a human who may write content may write, and no token role holds: an onboarding
 * walkthrough is a person's to click through, not a script's.
 */
const MEMBER_WRITABLE_MODELS = ["wizard"] as const;

/**
 * Models an org's integration manager reads. Not a tier: the reads travel with the
 * `integrationManager` add-on role, whose scopes an admin holds by seat role and a guest by grant.
 * Writing them is `integration:write`.
 */
const GRANT_MODELS = ["userReportSetting", "userReportToken"] as const;

/**
 * What a human observer may write. Read off the write guards as they stood before they became
 * bare membership questions, so nothing an observer could do silently went away. `timeTrackingSegment` is
 * on it on purpose: it is the failsafe for a member demoted to observer while a tracker is running,
 * who must still be able to stop it.
 */
const OBSERVER_WRITABLE_MODELS = [
  "cardSubscription",
  "deckSubscription",
  "handCard",
  "resolvable",
  "resolvableEntry",
  "resolvableEntryReaction",
  "resolvableParticipant",
  "timeTrackingSegment",
] as const;

/** Per-user rows. Layered on for user tokens only — an account token has no person behind it. */
const SELF_MODELS = [
  "accountUserSetting",
  "cardDiffNotification",
  "lastSeenCardUpvote",
  "projectSelection",
  "queueSelection",
  "resolvableNotification",
  "savedSearch",
  "userNotificationChannel",
] as const;

/** Desk writes on models whose *read* is everyone's business: reordering projects, own profile. */
const SELF_WRITE_ONLY_MODELS = ["projectOrder", "user"] as const;

/**
 * Per-user rows a person reads but never writes through a scope — a tracker the server maintains,
 * an onboarding step, their own tokens. Read-only, so no `<model>:write` is minted for them.
 */
const SELF_READ_ONLY_MODELS = [
  "accountUserAchievement",
  "activeTimeTracker",
  "apiToken",
  "autoFinishedTimeTrackingSegment",
  "passkey",
  "userDismissedHint",
  "userOnboarding",
] as const;

/**
 * Scopes the model lists do not produce: writes on the org and on models no tier list makes
 * writable, and every verb beyond `read` and `write`. A model gets a third verb only where some
 * credential has to hold or withhold that operation separately from `write`.
 *
 * - `account:readAdmin` — the `group: "admin"` fields of `account`; see `modelReadScope`.
 * - `account:write` — the org itself.
 * - `accountRole:write` — invitations, a member's role and add-on roles, disabling, re-enabling,
 *   removing. Named after `account_users`, where all of those are row transitions; `account:team`
 *   is a different thing, a reach level in `lib/guards.ts`.
 * - `accountApiToken:write` — the org's token policy: minting, editing and revoking the org's
 *   tokens, revoking a member's personal one, and the org-wide switch for personal tokens.
 * - `billing:write` — payment methods, the subscription, seat counts and the invoice previews.
 * - `integration:write` — setting up and removing the org's integrations, and the user-report
 *   settings that come with them.
 * - `project:create` — creating a project.
 * - `project:delete` — deleting a project. Archiving is `project:write`.
 * - `project:writeAccess` — changing a project's `defaultUserAccess`, which decides whether the
 *   whole team sees it. A producer edits the project, an admin decides who reaches it.
 * - `deck:delete` — deleting a deck. Held wherever `deck:write` is; a token can drop it alone.
 * - `file:delete` — deleting the stored file, not just detaching it from a card.
 * - `stripeAccountSync:readAdmin` — the `group: "admin"` field of `stripeAccountSync`
 *   (`paymentMethod`); see `modelReadScope`.
 * - `user:readSelf` — the `group: "self"` fields of `user`: a person's own settings, such as
 *   `timezone`. Held by every human and every personal token.
 * - `user:readSelfSecurity` — the `group: "selfSecurity"` fields of `user`: whether the account has
 *   a password or a second factor. Sessions only, like `passkey:read`: a personal token reaches one
 *   org, and knowing whether a password alone logs in helps turn a leaked one into the whole account.
 *
 * `accountApiToken:write` and `billing:write` are in `HUMAN_ONLY_SCOPES`: an `admin` credential
 * must not mint the next credential or move the org's money.
 */
const EXTRA_SCOPES = [
  "account:readAdmin",
  "stripeAccountSync:readAdmin",
  "user:readSelf",
  "user:readSelfSecurity",
  "account:write",
  "accountRole:write",
  "accountApiToken:write",
  "billing:write",
  "integration:write",
  "project:create",
  "project:delete",
  "project:writeAccess",
  "deck:delete",
  "file:delete",
] as const;

/**
 * The five a workspace-organizing bot writes on a member's behalf: subscribing them to the
 * announcements deck, pinning the current milestone, choosing whose queues they see, ordering
 * their projects, choosing which sit in their sidebar.
 */
const WORKSPACE_ORGANIZER_MODELS = [
  "deckSubscription",
  "pinnedMilestone",
  "projectOrder",
  "projectSelection",
  "queueSelection",
] as const;

/** A comment thread's rows, which an admin moderates: the comment, the participant, the reaction. */
const THREAD_MODELS = [
  "resolvableEntry",
  "resolvableParticipant",
  "resolvableEntryReaction",
] as const;

/**
 * Time logged or corrected on a teammate's behalf, which a producer does for their project. The one
 * `writeAny` that is project-scoped: it sits in `PROJECT_SCOPED_SCOPES` and is answered per project.
 */
const PRODUCER_ANY_WRITE_MODELS = ["timeTrackingSegment"] as const;

/**
 * Bookmarks are work assignment, which automations coordinate, and a bot has no bookmarks of its
 * own — so the token `user` tier holds the `writeAny` outright. No membership role does, which
 * leaves it to an org token: `clampToPerson` takes it back off a personal one. See
 * `TOKEN_ONLY_SCOPES`.
 */
const TOKEN_ANY_WRITE_MODELS = ["handCard"] as const;

/** Another member's inbox deck, which only an admin sets. Account-scoped, unlike a teammate's time. */
const ADMIN_ANY_WRITE_MODELS = ["accountUserSetting"] as const;

/** Owned models somebody may write on another person's behalf. Everywhere else `writeAny` is not minted. */
const ANY_WRITE_MODELS = [
  ...WORKSPACE_ORGANIZER_MODELS,
  ...THREAD_MODELS,
  ...PRODUCER_ANY_WRITE_MODELS,
  ...TOKEN_ANY_WRITE_MODELS,
  ...ADMIN_ANY_WRITE_MODELS,
] as const;

type AnyWriteModel = (typeof ANY_WRITE_MODELS)[number];

/**
 * Models whose rows belong to a person, so the write verb has to say *whose* row the call touches:
 * `writeOwn` for the caller's own, `writeAny` for somebody else's. A plain `write` is not minted
 * for them, so a declaration cannot forget to say which.
 *
 * Every model with a `user_id` that means "belongs to" is here, whether or not anyone may write
 * another person's row today — the verb says whose row it is, not whether a bot exists. The ones
 * without a `writeAny` holder refuse another person's row in the action body or in the `self` arm
 * of their repo guard.
 */
const OWNED_MODELS = [
  "cardDiffNotification",
  "cardSubscription",
  "cardUpvote",
  "resolvableNotification",
  "savedSearch",
  "user",
  "userNotificationChannel",
  "userTag",
  ...ANY_WRITE_MODELS,
] as const;

type OwnedModel = (typeof OWNED_MODELS)[number];

const OWNED_MODEL_SET: ReadonlySet<string> = new Set<string>(OWNED_MODELS);

type WritableModel =
  | (typeof USER_WRITABLE_MODELS)[number]
  | (typeof PRODUCER_WRITABLE_MODELS)[number]
  | (typeof OBSERVER_WRITABLE_MODELS)[number]
  | (typeof ADMIN_WRITABLE_MODELS)[number]
  | (typeof MEMBER_WRITABLE_MODELS)[number]
  | (typeof SELF_MODELS)[number]
  | (typeof SELF_WRITE_ONLY_MODELS)[number];

// an owned model that no list makes writable would silently lose its verb to `Extract` below
OWNED_MODELS satisfies readonly WritableModel[];

const readScope = <T extends string>(model: T) => `${model}:read` as const;

type WriteScope<T extends string> = T extends OwnedModel ? `${T}:writeOwn` : `${T}:write`;

/** The write verb a model's own kind decides: `writeOwn` where rows belong to a person, `write` otherwise. */
const writeScopes = <T extends string>(models: readonly T[]): WriteScope<T>[] =>
  models.map(
    (model) =>
      (OWNED_MODEL_SET.has(model) ? `${model}:writeOwn` : `${model}:write`) as WriteScope<T>
  );

/** The other half of an owned model's pair, held by a bot or an admin rather than by everyone. */
const anyWrites = <T extends AnyWriteModel>(models: readonly T[]) =>
  models.map((model) => `${model}:writeAny` as const);

/** Spelled out rather than `${string}:${verb}`, so a typo is a compile error. */
export type Scope =
  | `${
      | (typeof VISITOR_MODELS)[number]
      | (typeof PROJECT_MEMBER_MODELS)[number]
      | (typeof MEMBER_MODELS)[number]
      | (typeof ADMIN_READ_MODELS)[number]
      | (typeof GRANT_MODELS)[number]
      | (typeof SELF_MODELS)[number]
      | (typeof SELF_READ_ONLY_MODELS)[number]}:read`
  | `${PublicTierModel}:readPublic`
  | `${Exclude<WritableModel, OwnedModel>}:write`
  | `${Extract<WritableModel, OwnedModel>}:writeOwn`
  | `${AnyWriteModel}:writeAny`
  | (typeof EXTRA_SCOPES)[number];

const SELF_READ_SCOPES: Scope[] = [...SELF_MODELS.map(readScope), "user:readSelf"];
const SELF_WRITE_SCOPES: Scope[] = [
  ...writeScopes(SELF_MODELS),
  ...writeScopes(SELF_WRITE_ONLY_MODELS),
];

const readPublicScope = <T extends PublicTierModel>(model: T) => `${model}:readPublic` as const;

/** `<model>:read`, and `<model>:readPublic` next to it for a public-tier model: both tiers of it. */
const memberReadScopes = (models: readonly string[]): Scope[] =>
  models.flatMap((model) =>
    isPublicTierModel(model)
      ? [readScope(model), readPublicScope(model)]
      : [readScope(model) as Scope]
  );

const SCOPE_SET: ReadonlySet<string> = new Set<Scope>([
  ...memberReadScopes(VISITOR_MODELS),
  ...PROJECT_MEMBER_MODELS.map(readScope),
  ...MEMBER_MODELS.map(readScope),
  ...ADMIN_READ_MODELS.map(readScope),
  ...GRANT_MODELS.map(readScope),
  ...SELF_READ_ONLY_MODELS.map(readScope),
  ...writeScopes(USER_WRITABLE_MODELS),
  ...writeScopes(PRODUCER_WRITABLE_MODELS),
  ...writeScopes(OBSERVER_WRITABLE_MODELS),
  ...writeScopes(ADMIN_WRITABLE_MODELS),
  ...writeScopes(MEMBER_WRITABLE_MODELS),
  ...anyWrites(ANY_WRITE_MODELS),
  ...SELF_READ_SCOPES,
  ...SELF_WRITE_SCOPES,
  ...EXTRA_SCOPES,
]);

export const isScope = (value: string): value is Scope => SCOPE_SET.has(value);

/**
 * The roles a token can be issued with. Named after `user_account_role` so that deriving a
 * session's scopes from `account_users.role` later is a no-op.
 */
export const TOKEN_ROLES = ["observer", "user", "producer", "admin"] as const;
export type TokenRole = (typeof TOKEN_ROLES)[number];

const TOKEN_ROLE_SET: ReadonlySet<string> = new Set(TOKEN_ROLES);

export const isTokenRole = (value: string): value is TokenRole => TOKEN_ROLE_SET.has(value);

const observerScopes: Scope[] = [
  ...memberReadScopes(PROJECT_MODELS),
  ...PROJECT_MEMBER_MODELS.map(readScope),
];
const userScopes: Scope[] = [
  ...observerScopes,
  ...writeScopes(USER_WRITABLE_MODELS),
  ...anyWrites(TOKEN_ANY_WRITE_MODELS),
];
const producerScopes: Scope[] = [
  ...userScopes,
  ...writeScopes(PRODUCER_WRITABLE_MODELS),
  ...anyWrites(PRODUCER_ANY_WRITE_MODELS),
  "deck:delete",
];
const adminScopes: Scope[] = [
  ...producerScopes,
  "project:create",
  "project:delete",
  "project:writeAccess",
  "file:delete",
  "account:write",
  "account:readAdmin",
  "accountRole:write",
  ...ADMIN_READ_MODELS.map(readScope),
];

export const ROLE_SCOPES: Record<TokenRole, ReadonlySet<Scope>> = {
  observer: new Set(observerScopes),
  user: new Set(userScopes),
  producer: new Set(producerScopes),
  admin: new Set(adminScopes),
};

/**
 * The same tiers plus the desk, for a credential that is a person. Desk *writes* start at `user`,
 * so a token labelled "Read" writes nothing at all.
 */
const withDesk = (role: TokenRole): ReadonlySet<Scope> =>
  new Set<Scope>([
    ...ROLE_SCOPES[role],
    ...SELF_READ_SCOPES,
    ...(role === "observer" ? [] : SELF_WRITE_SCOPES),
  ]);

export const USER_TOKEN_ROLE_SCOPES: Record<TokenRole, ReadonlySet<Scope>> = {
  observer: withDesk("observer"),
  user: withDesk("user"),
  producer: withDesk("producer"),
  admin: withDesk("admin"),
};

/**
 * The scopes answered by a `user_project_access` row rather than by the membership row — asked once
 * per project the request touches, never once per org.
 *
 * Everything else is account-scoped. The split is what keeps full staff and per-project producer
 * additive: a guest producing on *Game* in a `staffPermission: 'full'` org holds both halves, which
 * a single ranked list of roles would lose.
 */
export const PROJECT_SCOPED_SCOPES = [
  "deck:delete",
  "deck:write",
  "deckGuardian:write",
  "forecastOverwrite:write",
  "milestone:write",
  "project:write",
  "projectTag:write",
  "projectUser:write",
  "sprint:write",
  "sprintConfig:write",
  "timeTrackingSegment:writeAny",
  "workflowItem:write",
] as const satisfies readonly Scope[];

export type ProjectScopedScope = (typeof PROJECT_SCOPED_SCOPES)[number];

const PROJECT_SCOPED_SCOPE_SET: ReadonlySet<Scope> = new Set<Scope>(PROJECT_SCOPED_SCOPES);

export const isProjectScopedScope = (scope: Scope): scope is ProjectScopedScope =>
  PROJECT_SCOPED_SCOPE_SET.has(scope);

/**
 * What `staffPermission: 'full'` grants everywhere in the org: editing the structure *inside* a
 * project. The project itself, its membership, its guardian lists, its forecast scenarios and a
 * teammate's time stay with producers and admins, which is why `project:write`,
 * `projectUser:write`, `deckGuardian:write`, `forecastOverwrite:write` and
 * `timeTrackingSegment:writeAny` are not on it.
 *
 * Held on the account set, where it acts as a bypass marker — the descriptor skips the
 * `user_project_access` read for any scope in it.
 */
export const FULL_STAFF_SCOPES = [
  "deck:delete",
  "deck:write",
  "milestone:write",
  "projectTag:write",
  "sprint:write",
  "sprintConfig:write",
  "workflowItem:write",
] as const satisfies readonly ProjectScopedScope[];

/**
 * Roles held in addition to a seat role or a token tier: additive, never a seat. Stored by name —
 * `account_users.add_roles` for a member, next to the tier in `api_tokens.roles` for a token — so a
 * scope added to one reaches every holder on their next request without a row changing.
 */
export const ADDON_ROLES = [
  "integrationManager",
  "journeyTemplateEditor",
  "affiliateManager",
  "workspaceOrganizer",
] as const;

export type AddonRole = (typeof ADDON_ROLES)[number];

const ADDON_ROLE_SET: ReadonlySet<string> = new Set(ADDON_ROLES);

export const isAddonRole = (value: string): value is AddonRole => ADDON_ROLE_SET.has(value);

/** What `api_tokens.roles` may hold: a tier, and any add-on roles. */
export const TOKEN_ROLE_NAMES = [...TOKEN_ROLES, ...ADDON_ROLES] as const;
export type TokenRoleName = (typeof TOKEN_ROLE_NAMES)[number];

/** `staff` is never stored: every guest of a `staffPermission: 'full'` org holds it. */
export const ADDON_ROLE_SCOPES: Record<AddonRole | "staff", readonly Scope[]> = {
  // The write, plus a read of every model integration setup touches. Free for a human, who holds
  // these reads as a member; the whole point for a token, whose tiers list content models only.
  integrationManager: [
    "integration:write",
    "integration:read",
    "discordGuild:read",
    "discordGuild:readPublic",
    "discordMember:read",
    "discordProjectNotification:read",
    "discordSlashCommand:read",
    "jiraSite:read",
    "jiraProjectSync:read",
    "userReportEmail:read",
    "userReportSetting:read",
    "userReportToken:read",
  ],
  // Account-scoped: `workflow_templates` has no `project_id`, so no descriptor can ask it per project.
  journeyTemplateEditor: ["workflowTemplate:write"],
  affiliateManager: ["affiliateCode:read", "affiliateCode:write", "affiliateCodeStat:read"],
  // Another member's workspace state, for an org's bot setting up a new member. No person holds
  // it: there is no UI for organizing somebody else's workspace.
  workspaceOrganizer: anyWrites(WORKSPACE_ORGANIZER_MODELS),
  staff: [...FULL_STAFF_SCOPES, "workflowTemplate:write"],
};

/**
 * The add-on roles an admin may give a member. The other two are for an org's own token: an admin
 * holds `affiliateManager`'s scopes by seat role, and no person holds `workspaceOrganizer`'s.
 */
export const MEMBER_ADDON_ROLES = [
  "integrationManager",
  "journeyTemplateEditor",
] as const satisfies readonly AddonRole[];

export type MemberAddonRole = (typeof MEMBER_ADDON_ROLES)[number];

const MEMBER_ADDON_ROLE_SET: ReadonlySet<string> = new Set(MEMBER_ADDON_ROLES);

/** Also the read guard for `account_users.add_roles`: a role dropped from the list stops counting. */
export const isMemberAddonRole = (value: string): value is MemberAddonRole =>
  MEMBER_ADDON_ROLE_SET.has(value);

const SELF_TIER_READ_SCOPES: Scope[] = [
  ...SELF_READ_SCOPES,
  ...SELF_READ_ONLY_MODELS.map(readScope),
  "user:readSelfSecurity",
];

/**
 * An anonymous Open Decks reader, a logged-in non-member, and any human on a route that names no
 * org. `cardUpvote:writeOwn` is in it because `votes.vote` requires a login, not a membership.
 * `user:readSelf` is in it because the app renders a logged-in non-member's own settings on an Open
 * Decks page; the censor applies it to the caller's own row only, which an anonymous reader lacks.
 */
export type VisitorScope =
  | `${Exclude<(typeof VISITOR_MODELS)[number], PublicTierModel>}:read`
  | `${PublicTierModel}:readPublic`
  | "cardUpvote:writeOwn"
  | "user:readSelf";

export const VISITOR_SCOPES: ReadonlySet<Scope> = new Set<VisitorScope>([
  ...VISITOR_MODELS.filter(
    (model): model is Exclude<(typeof VISITOR_MODELS)[number], PublicTierModel> =>
      !isPublicTierModel(model)
  ).map(readScope),
  ...PUBLIC_TIER_MODELS.map(readPublicScope),
  "cardUpvote:writeOwn",
  "user:readSelf",
]);

const sessionObserver: Scope[] = [
  ...VISITOR_SCOPES,
  ...PUBLIC_TIER_MODELS.map(readScope),
  ...PROJECT_MEMBER_MODELS.map(readScope),
  ...MEMBER_MODELS.map(readScope),
  ...SELF_TIER_READ_SCOPES,
  ...SELF_WRITE_SCOPES,
  ...writeScopes(OBSERVER_WRITABLE_MODELS),
];

const sessionUser: Scope[] = [
  ...sessionObserver,
  ...writeScopes(USER_WRITABLE_MODELS),
  ...writeScopes(MEMBER_WRITABLE_MODELS),
];

const sessionAdmin: Scope[] = [
  ...sessionUser,
  ...writeScopes(ADMIN_WRITABLE_MODELS),
  // moderating a thread is what an admin is for: editing a comment, marking a participant done,
  // removing a reaction
  ...anyWrites(THREAD_MODELS),
  ...anyWrites(ADMIN_ANY_WRITE_MODELS),
  // an admin produces everywhere, which is what `ADMIN_ROLES.has(role)` says in every guard today
  ...PROJECT_SCOPED_SCOPES,
  ...ADDON_ROLE_SCOPES.journeyTemplateEditor,
  "project:create",
  "project:delete",
  "project:writeAccess",
  "file:delete",
  "account:write",
  "account:readAdmin",
  "stripeAccountSync:readAdmin",
  "accountRole:write",
  "billing:write",
  "accountApiToken:write",
  ...ADMIN_READ_MODELS.map(readScope),
  ...ADDON_ROLE_SCOPES.integrationManager,
];

/**
 * A human's account-level set, by membership role. Keyed by `UserRole` rather than `TokenRole`
 * because the two diverge on purpose: there is no `producer` entry (producing is per project and
 * lives in `projectScopes`), and an `admin` holds what no token list holds — `billing:write` and
 * `accountApiToken:write`.
 */
export const SESSION_ROLE_SCOPES: Record<"observer" | "user" | "admin", ReadonlySet<Scope>> = {
  observer: new Set(sessionObserver),
  user: new Set(sessionUser),
  admin: new Set(sessionAdmin),
};

/**
 * The account-scoped half of a human's set: the seat role, plus the add-on roles for a guest.
 *
 * Add-on roles count for guests only: an admin holds their scopes by seat role, and `add_roles`
 * survives a demotion to observer. In a `full` org every guest holds `staff`, whose
 * `FULL_STAFF_SCOPES` land on the *account* set even though they are project-scoped.
 */
export const accountScopes = (opts: {
  role: UserRole | null;
  addRoles: readonly MemberAddonRole[];
  staffPermission: string;
}): ReadonlySet<Scope> => {
  const {role, addRoles, staffPermission} = opts;
  switch (role) {
    case "owner":
    case "admin":
      return SESSION_ROLE_SCOPES.admin;
    case "observer":
      return SESSION_ROLE_SCOPES.observer;
    case "guest": {
      const scopes = new Set<Scope>(SESSION_ROLE_SCOPES.user);
      const held = staffPermission === "full" ? [...addRoles, "staff" as const] : addRoles;
      for (const addonRole of held) {
        for (const scope of ADDON_ROLE_SCOPES[addonRole]) scopes.add(scope);
      }
      return scopes;
    }
    default:
      // no row, `service`, `token`, anything unrecognised
      return VISITOR_SCOPES;
  }
};

const NO_SCOPES: ReadonlySet<Scope> = new Set<Scope>();

/**
 * The project-scoped half, for one project: the `user_project_access` row for `(userId, projectId)`.
 *
 * Additive rather than ranked — a guest producer in a `full` org holds both halves.
 */
export const projectScopes = (opts: {
  role: UserRole | null;
  projectRole: UserProjectRole | null;
  staffPermission: string;
}): ReadonlySet<Scope> => {
  const {role, projectRole, staffPermission} = opts;
  if (role === "owner" || role === "admin") return PROJECT_SCOPED_SCOPE_SET;
  if (role !== "guest") return NO_SCOPES;
  const scopes = new Set<Scope>(staffPermission === "full" ? FULL_STAFF_SCOPES : []);
  if (projectRole === "producer") for (const scope of PROJECT_SCOPED_SCOPES) scopes.add(scope);
  return scopes;
};

/**
 * Scopes that only a person may hold, so that an action declaring one needs no second check on
 * which credential is calling. Moving the org's money and minting the next credential are the two
 * operations a script is never handed, however deliberately an admin would grant them.
 *
 * In no tier or add-on role list, which the check below enforces: a token's set is built from
 * those lists alone, so none can contain one.
 */
export const HUMAN_ONLY_SCOPES = [
  "billing:write",
  "accountApiToken:write",
] as const satisfies readonly Scope[];

const HUMAN_ONLY_SCOPE_SET: ReadonlySet<Scope> = new Set<Scope>(HUMAN_ONLY_SCOPES);

export const isHumanOnlyScope = (scope: Scope): boolean => HUMAN_ONLY_SCOPE_SET.has(scope);

// The list is only a wall while no list a token can name carries an entry. Refuse to boot rather
// than serve a set that has stopped meaning it.
for (const table of [ROLE_SCOPES, USER_TOKEN_ROLE_SCOPES, ADDON_ROLE_SCOPES]) {
  for (const [role, scopes] of Object.entries(table)) {
    for (const scope of scopes) {
      if (isHumanOnlyScope(scope)) {
        throw new Error(`'${scope}' is in HUMAN_ONLY_SCOPES and cannot be in the '${role}' role`);
      }
    }
  }
}

/**
 * Scopes a personal token's tier names that no membership role holds, so `clampToPerson` drops
 * them from every personal token however senior its owner. Their absence from a clamped set says
 * nothing about the person's role, which is the one thing `ceilingTier` needs to know.
 */
export const TOKEN_ONLY_SCOPES = ["handCard:writeAny"] as const satisfies readonly Scope[];

const TOKEN_ONLY_SCOPE_SET: ReadonlySet<Scope> = new Set<Scope>(TOKEN_ONLY_SCOPES);

export const isTokenOnlyScope = (scope: Scope): boolean => TOKEN_ONLY_SCOPE_SET.has(scope);

// A tier scope that reaches neither an admin nor a project row and is missing from the list above
// reads as a demotion, and `ceilingTier` offers every member the bottom tier. Name it or fail here.
for (const [tier, scopes] of Object.entries(USER_TOKEN_ROLE_SCOPES)) {
  for (const scope of scopes) {
    const reachable =
      SESSION_ROLE_SCOPES.admin.has(scope) ||
      PROJECT_SCOPED_SCOPE_SET.has(scope) ||
      isTokenOnlyScope(scope);
    if (!reachable) {
      throw new Error(
        `'${scope}' is in the '${tier}' tier and in no membership role: add it to TOKEN_ONLY_SCOPES`
      );
    }
  }
}

/**
 * Cap a personal token's scopes at what the person themselves may do.
 *
 * Two layers, because the person's set has two axes. The account-scoped half is intersected
 * exactly; the project-scoped half passes through untouched and is answered per project at
 * dispatch, against the person's own `user_project_access` row — a token's `ctx.userId` *is* the
 * human. Clamping that half here would mean re-running the union query over their projects on
 * every bearer request, only to make the same refusal arrive earlier.
 *
 * An intersection at auth rather than a rejection at creation, so an observer's `admin`-tier token
 * grows into its tier if that person is promoted.
 */
export const clampToPerson = (
  tokenScopes: ReadonlySet<Scope>,
  personScopes: ReadonlySet<Scope>
): ReadonlySet<Scope> => {
  const clamped = new Set<Scope>();
  for (const scope of tokenScopes) {
    if (personScopes.has(scope) || PROJECT_SCOPED_SCOPE_SET.has(scope)) clamped.add(scope);
  }
  return clamped;
};

/**
 * Does a credential's scope set permit `required`?
 *
 * `null` is unrestricted — a session, a legacy token, an Open Decks reader, a ctx no request drove.
 * A human's ceiling is their role and the guards, not a scope set.
 */
export const hasScope = (held: ReadonlySet<Scope> | null, required: Scope): boolean =>
  held === null || held.has(required);
